import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiArrowRight, FiCheck } = FiIcons;

// Paraphrased questions to avoid copyright issues
const questions = [
  {
    id: 1,
    text: "Wie häufig ändern Sie Ihre beruflichen Ziele?",
    options: [
      { text: "Sehr häufig", value: 5 },
      { text: "Häufig", value: 4 },
      { text: "Gelegentlich", value: 3 },
      { text: "Selten", value: 2 },
      { text: "Nie", value: 1 }
    ]
  },
  {
    id: 2,
    text: "Wie klar definiert sind Ihre Karriereziele?",
    options: [
      { text: "Völlig unklar", value: 5 },
      { text: "Eher unklar", value: 4 },
      { text: "Teilweise klar", value: 3 },
      { text: "Ziemlich klar", value: 2 },
      { text: "Sehr klar", value: 1 }
    ]
  },
  {
    id: 3,
    text: "Wie gut wissen Sie, welche Schritte für Ihre Karriereziele nötig sind?",
    options: [
      { text: "Überhaupt nicht", value: 5 },
      { text: "Kaum", value: 4 },
      { text: "Teilweise", value: 3 },
      { text: "Ziemlich gut", value: 2 },
      { text: "Sehr gut", value: 1 }
    ]
  },
  {
    id: 4,
    text: "Haben Sie eine konkrete Strategie für Ihre Karriereentwicklung?",
    options: [
      { text: "Gar keine", value: 5 },
      { text: "Kaum eine", value: 4 },
      { text: "Eine grobe", value: 3 },
      { text: "Eine ziemlich klare", value: 2 },
      { text: "Eine sehr detaillierte", value: 1 }
    ]
  },
  {
    id: 5,
    text: "Wie strukturiert ist Ihr Karriereplan?",
    options: [
      { text: "Gar nicht strukturiert", value: 5 },
      { text: "Wenig strukturiert", value: 4 },
      { text: "Teilweise strukturiert", value: 3 },
      { text: "Gut strukturiert", value: 2 },
      { text: "Sehr gut strukturiert", value: 1 }
    ]
  },
  {
    id: 6,
    text: "Wie sicher sind Sie sich über Ihre berufliche Richtung?",
    options: [
      { text: "Völlig unsicher", value: 5 },
      { text: "Eher unsicher", value: 4 },
      { text: "Teilweise sicher", value: 3 },
      { text: "Ziemlich sicher", value: 2 },
      { text: "Sehr sicher", value: 1 }
    ]
  },
  {
    id: 7,
    text: "Wie gut können Sie sich an berufliche Veränderungen anpassen?",
    options: [
      { text: "Sehr schlecht", value: 5 },
      { text: "Schlecht", value: 4 },
      { text: "Durchschnittlich", value: 3 },
      { text: "Gut", value: 2 },
      { text: "Sehr gut", value: 1 }
    ]
  },
  {
    id: 8,
    text: "Wie reagieren Sie auf neue Arbeitsbedingungen?",
    options: [
      { text: "Sehr negativ", value: 5 },
      { text: "Eher negativ", value: 4 },
      { text: "Neutral", value: 3 },
      { text: "Eher positiv", value: 2 },
      { text: "Sehr positiv", value: 1 }
    ]
  },
  {
    id: 9,
    text: "Wie offen sind Sie für neue berufliche Herausforderungen?",
    options: [
      { text: "Gar nicht offen", value: 5 },
      { text: "Wenig offen", value: 4 },
      { text: "Teilweise offen", value: 3 },
      { text: "Ziemlich offen", value: 2 },
      { text: "Sehr offen", value: 1 }
    ]
  }
];

const TestPage = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (value) => {
    setSelectedOption(value);
  };

  const handleNext = () => {
    if (selectedOption !== null) {
      setAnswers(prev => ({
        ...prev,
        [questions[currentQuestion].id]: selectedOption
      }));

      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
        setSelectedOption(null);
      } else {
        // Calculate total score and navigate to results
        const totalScore = Object.values({...answers, [questions[currentQuestion].id]: selectedOption})
          .reduce((sum, score) => sum + score, 0);
        
        navigate('/results', { state: { score: totalScore } });
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      setSelectedOption(answers[questions[currentQuestion - 1].id] || null);
    }
  };

  const isLastQuestion = currentQuestion === questions.length - 1;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-gray-900">Karriereplanungstest</h1>
            <div className="flex items-center gap-4">
              <Link to="/impressum" className="text-sm text-gray-500 hover:text-blue-600 hidden sm:block">Impressum</Link>
              <span className="text-sm text-gray-600">
                Frage {currentQuestion + 1} von {questions.length}
              </span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-blue-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="max-w-2xl mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg shadow-lg p-8"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-8 leading-relaxed">
                {questions[currentQuestion].text}
              </h2>

              <div className="space-y-3">
                {questions[currentQuestion].options.map((option, index) => (
                  <motion.button
                    key={index}
                    onClick={() => handleAnswer(option.value)}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                      selectedOption === option.value
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{option.text}</span>
                      {selectedOption === option.value && (
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    currentQuestion === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
                  Zurück
                </button>

                <button
                  onClick={handleNext}
                  disabled={selectedOption === null}
                  className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    selectedOption === null
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {isLastQuestion ? 'Auswertung' : 'Weiter'}
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default TestPage;